import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import useEnsureSearchThreadId from '@webclient/components/feedback/hooks/useEnsureSearchThreadId';
import { notification } from '@webclient/components/notification';

import { storage } from '@webclient/lib/agent';
import CONFIG from '@webclient/lib/const/uppy';
import getLocale from '@webclient/lib/getUppyLocale';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useGetCredit from '@webclient/hooks/useGetCredit';

import { setUploadedToKnowledgeBaseByUserId } from '@webclient/state/slices/app-settings';
import { selectShouldSaveUploadsToKnowledgeBase } from '@webclient/state/slices/app-settings/userSettingSelector';
import {
  addAttachment,
  deleteAttachmentById,
  sendAttachmentToRoom,
  updateAttachmentByName,
  updateAttachmentProgress,
} from '@webclient/state/slices/search-session';
import searchAsyncOrchestrator from '@webclient/state/slices/search-session/thunks/searchAsyncOrchestrator';

import { LanguageType } from '@webclient/shared-types/translation';

import { PayloadAction } from '@reduxjs/toolkit';
import { useTolgee, useTranslate } from '@tolgee/react';
import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';
import DropTarget from '@uppy/drop-target';
import Webcam from '@uppy/webcam';

type ParametersReturnType = {
  method: 'PUT';
  headers: Record<string, string>;
  fields: { key: string };
  url: string;
} | null;

export default function useSharedUppyInstance() {
  const dispatch = useAppDispatch();
  const shouldSaveUploadsToKnowledgeBase = useAppSelector(
    selectShouldSaveUploadsToKnowledgeBase,
  );
  const me = useAppSelector((state) => state.auth.me);
  const [isDragging, setIsDragging] = useState(false);
  const { getLanguage } = useTolgee();
  const { t } = useTranslate();
  const { id: threadIdFromParams } = useParams<{ id: string }>();
  const threadId = useEnsureSearchThreadId();
  const { getAccountType } = useGetCredit();
  const isFreeAccount =
    getAccountType() === 'free' || getAccountType() === 'visitor';
  const MAX_FILE_SIZE = isFreeAccount
    ? CONFIG.MAX_FILE_SIZE.notPaid
    : CONFIG.MAX_FILE_SIZE.paid;
  async function retrieveThreadIdAfterCreation() {
    const { payload } = (await dispatch(
      searchAsyncOrchestrator(
        threadIdFromParams
          ? {
              requestOrigin: 'search',
              searchThreadId: threadIdFromParams,
            }
          : {
              requestOrigin: 'home',
            },
      ),
    )) as PayloadAction<{
      threadId: string;
      token: string;
    }>;

    return payload.threadId;
  }

  useEffect(() => {
    (function keepSaveUploadsToKnowledgeBaseUpdatedEveryTimeUserChangesSettings() {
      if (!me) return;
      dispatch(
        setUploadedToKnowledgeBaseByUserId({
          userId: me.uuid,
          shouldSave: shouldSaveUploadsToKnowledgeBase,
        }),
      );
    })();
  }, [dispatch, me, shouldSaveUploadsToKnowledgeBase]);

  const [uppy] = useState(() => {
    let entityId: string | undefined;
    return new Uppy({
      restrictions: {
        maxFileSize: MAX_FILE_SIZE,
        maxTotalFileSize: MAX_FILE_SIZE,
        allowedFileTypes: CONFIG.FILE_RESTRICTIONS as never,
      },
      locale: getLocale(getLanguage() as LanguageType),
    })
      .use(Webcam, {
        modes: ['picture'],
        mirror: true,
        videoConstraints: {
          facingMode: 'user',
        },
      })
      .use(AwsS3, {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        /* @ts-expect-error */ // TODO: fix types
        async getUploadParameters(file): Promise<ParametersReturnType> {
          const size = file.data.size;
          const sizeInKB = size / 1024;
          const name = file.data.name ?? file.name;
          const type = file.type;

          const { data: policyData } = await storage.createPolicy({
            name,
            size: sizeInKB,
            mimeType: type,
            context: {
              entityType: 'thread',
              entityId,
              usage: 'file',
              saveAsSource: shouldSaveUploadsToKnowledgeBase,
            },
          });
          return {
            method: 'PUT',
            headers: {
              'Content-Type': type,
            },
            fields: {
              key: policyData.key,
            },
            url: policyData.url,
          };
        },
      })
      .on('restriction-failed', (_file, error) => {
        console.error(_file, error);
        notification.error({ message: error.message, id: 'file-restriction' });
      })
      .on('upload-progress', (file, progress) => {
        if (!progress.bytesTotal) return;
        if (!file) {
          throw new Error('File not found');
        }

        dispatch(
          updateAttachmentProgress({
            id: file.id,
            name: file.data.name || file.name || 'no-name',
            progress: Math.round(
              (progress.bytesUploaded / progress.bytesTotal) * 50, //? 50% for uploading, 50% for processing by ai
            ),
            error: null,
          }),
        );
      })
      .on('files-added', async (files) => {
        files.forEach((file) => {
          dispatch(
            addAttachment({
              id: file.id,
              name: file.data.name,
              type: file.data.type,
              isPending: true,
              progress: 0,
            }),
          );
        });
        entityId = threadId ? threadId : await retrieveThreadIdAfterCreation();
        const uploadedFiles = await uppy.upload().catch((err) => {
          console.error(err);
        });
        uploadedFiles?.successful?.forEach((file) => {
          if (!file.response?.body) return;
          dispatch(
            sendAttachmentToRoom({
              id: file.id,
              key: file.response.body.key as string,
              mimeType: file.type,
              name: file.data.name,
            }),
          );
        });
      })
      .on('upload-error', (file, error) => {
        if (!file) {
          throw new Error('File not found');
        }
        console.error(error);
        dispatch(
          deleteAttachmentById({
            id: file.id,
          }),
        );
        notification.error({
          message: t(
            'layout.searchWidget.attachments.errors.uploadFailed',
            'Upload failed, please try again',
          ),
        });
      })
      .on('upload-success', (file, response) => {
        if (!file) {
          throw new Error('File not found');
        }
        const { body } = response;
        if (!body) return;
        const key =
          body.key as NonNullable<ParametersReturnType>['fields']['key'];
        dispatch(
          updateAttachmentByName({
            id: file.id,
            name: file.data.name,
            key,
            isProcessingByAI: true,
            progress: 50, //? 50% for uploading, 50% for processing by ai
            type: file.data.type,
            error: null,
            isPending: false,
          }),
        );
      });
  });

  const dropTargetCallbackRef = useCallback(() => {
    if (!uppy.getPlugin('DropTarget')) {
      uppy.use(DropTarget, {
        target: document.body,
        onDragOver: () => {
          setIsDragging(true);
        },
        onDragLeave: () => {
          setIsDragging(false);
        },
        onDrop: () => {
          setIsDragging(false);
        },
      });
    }
  }, [uppy]);

  return { uppy, dropTargetCallbackRef, isDragging };
}
